import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent } from '@saas-pages/shared/ui'
import { StopwatchAnalyticsSession } from '@/pages/stopwatch/model/stopwatchAnalytics'
import { useT } from '@/shared/i18n'
import { releasePointerFocus, useHotkeys } from '@/shared/lib/hotkeys'
import { HotkeyHints } from '@/shared/ui/HotkeyHints'

const COPY_FEEDBACK_MS = 2000

interface StopwatchLap {
  number: number
  elapsedMs: number
  splitMs: number
}

function formatStopwatchClock(totalMs: number): { main: string; milliseconds: string } {
  const safeMs = Math.max(0, Math.floor(totalMs))
  const totalSeconds = Math.floor(safeMs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const milliseconds = safeMs % 1000

  return {
    main: [hours, minutes, seconds]
      .map((part) => part.toString().padStart(2, '0'))
      .join(':'),
    milliseconds: milliseconds.toString().padStart(3, '0'),
  }
}

function formatLapTime(totalMs: number): string {
  const clock = formatStopwatchClock(totalMs)
  return `${clock.main}.${clock.milliseconds}`
}

export function StopwatchPage() {
  const t = useT()
  const [stopwatchMs, setStopwatchMs] = useState(0)
  const [isRunning, setIsRunning] = useState(false)
  const [laps, setLaps] = useState<StopwatchLap[]>([])
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')

  const copyResetRef = useRef(0)
  const startedAtRef = useRef(0)
  const stopwatchBaseMsRef = useRef(stopwatchMs)
  const nextLapNumberRef = useRef(1)
  const originalTitleRef = useRef<string | null>(null)
  const analyticsSessionRef = useRef(new StopwatchAnalyticsSession())
  const stopwatchClock = formatStopwatchClock(stopwatchMs)

  useEffect(() => {
    originalTitleRef.current ??= document.title

    if (isRunning) {
      document.title = `${stopwatchClock.main}.${stopwatchClock.milliseconds} - ${t.stopwatch.title}`
      return
    }

    if (originalTitleRef.current) {
      document.title = originalTitleRef.current
    }
  }, [isRunning, stopwatchClock.main, stopwatchClock.milliseconds, t.stopwatch.title])

  useEffect(() => {
    return () => {
      analyticsSessionRef.current.end()
      window.clearTimeout(copyResetRef.current)
      if (originalTitleRef.current) {
        document.title = originalTitleRef.current
      }
    }
  }, [])

  useEffect(() => {
    if (!isRunning) return

    let frameId = 0

    const updateStopwatch = () => {
      const now = performance.now()
      setStopwatchMs(stopwatchBaseMsRef.current + now - startedAtRef.current)
      frameId = window.requestAnimationFrame(updateStopwatch)
    }

    frameId = window.requestAnimationFrame(updateStopwatch)

    return () => window.cancelAnimationFrame(frameId)
  }, [isRunning])

  function getCurrentStopwatchMs() {
    if (!isRunning) return stopwatchMs

    return stopwatchBaseMsRef.current + performance.now() - startedAtRef.current
  }

  function handleStart() {
    const now = performance.now()
    startedAtRef.current = now
    stopwatchBaseMsRef.current = stopwatchMs
    analyticsSessionRef.current.start()
    setIsRunning(true)
  }

  function handlePause() {
    const now = performance.now()
    const elapsedMs = stopwatchBaseMsRef.current + now - startedAtRef.current
    setStopwatchMs(elapsedMs)
    setIsRunning(false)
    if (elapsedMs > 0) analyticsSessionRef.current.recordMeasurement()
  }

  function handleLap() {
    const elapsedMs = getCurrentStopwatchMs()

    if (elapsedMs <= 0) return

    analyticsSessionRef.current.recordMeasurement()

    const lapNumber = nextLapNumberRef.current
    nextLapNumberRef.current += 1

    setStopwatchMs(elapsedMs)
    setLaps((currentLaps) => {
      const previousElapsedMs = currentLaps[0]?.elapsedMs ?? 0

      return [
        {
          number: lapNumber,
          elapsedMs,
          splitMs: elapsedMs - previousElapsedMs,
        },
        ...currentLaps,
      ]
    })
  }

  function handleReset() {
    const elapsedMs = getCurrentStopwatchMs()

    if (elapsedMs > 0) analyticsSessionRef.current.recordMeasurement()
    analyticsSessionRef.current.end()

    setIsRunning(false)
    setStopwatchMs(0)
    setLaps([])
    startedAtRef.current = 0
    stopwatchBaseMsRef.current = 0
    nextLapNumberRef.current = 1
    setCopyState('idle')
  }

  function handleToggle() {
    if (isRunning) handlePause()
    else handleStart()
  }

  async function handleCopyLaps() {
    const header = [t.stopwatch.lap, t.stopwatch.total, t.stopwatch.split].join('\t')
    const rows = [...laps]
      .reverse()
      .map((lap) => [lap.number, formatLapTime(lap.elapsedMs), formatLapTime(lap.splitMs)].join('\t'))

    try {
      await navigator.clipboard.writeText([header, ...rows].join('\n'))
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
    window.clearTimeout(copyResetRef.current)
    copyResetRef.current = window.setTimeout(() => setCopyState('idle'), COPY_FEEDBACK_MS)
  }

  useHotkeys({
    Space: handleToggle,
    KeyL: () => {
      if (isRunning || stopwatchMs > 0) handleLap()
    },
    KeyR: handleReset,
  })

  return (
    <div className="mx-auto max-w-4xl">
      <section className="pb-4 pt-1 sm:pb-8 sm:pt-4">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">
          {t.common.localBadge}
        </div>
        <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">
          {t.stopwatch.title}
        </h1>
        <p className="mt-1 line-clamp-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">
          {t.stopwatch.description}
        </p>
      </section>

      <Card className="overflow-hidden">
        <div
          aria-hidden="true"
          className="h-1.5 bg-gradient-to-r from-cyan-400 via-primary to-violet-400"
        />
        <CardContent className="space-y-3 p-3 sm:space-y-4 sm:p-6">
          <div className="rounded-lg border bg-background/70 px-2 py-5 text-center text-foreground shadow-inner dark:bg-background/80 sm:py-8">
            <div className="flex items-baseline justify-center whitespace-nowrap font-mono text-[clamp(2.25rem,11.5vw,5.5rem)] font-bold leading-none tabular-nums">
              <span>{stopwatchClock.main}</span>
              <span className="text-[0.62em] text-cyan-700 dark:text-cyan-200">
                .{stopwatchClock.milliseconds}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-2 sm:gap-3">
            <Button
              type="button"
              onClick={(event) => {
                releasePointerFocus(event)
                handleToggle()
              }}
              className="h-12 text-base"
            >
              {isRunning ? t.stopwatch.pause : t.stopwatch.start}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={(event) => {
                releasePointerFocus(event)
                handleLap()
              }}
              disabled={!isRunning && stopwatchMs <= 0}
              className="h-12 text-base"
            >
              {t.stopwatch.lap}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={(event) => {
                releasePointerFocus(event)
                handleReset()
              }}
              className="h-12 text-base"
            >
              {t.stopwatch.reset}
            </Button>
          </div>

          <HotkeyHints
            label={t.common.hotkeys.label}
            items={[
              { key: t.common.hotkeys.space, action: t.common.hotkeys.startPause },
              { key: 'L', action: t.common.hotkeys.lap },
              { key: 'R', action: t.common.hotkeys.reset },
            ]}
          />

          {laps.length > 0 && (
            <section className="rounded-lg border bg-muted/30 p-3 sm:p-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold tracking-normal">
                  {t.stopwatch.lapsTitle}
                  <span className="ml-2 font-mono text-xs font-normal tabular-nums text-muted-foreground">
                    {laps.length}
                  </span>
                </h2>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(event) => {
                    releasePointerFocus(event)
                    void handleCopyLaps()
                  }}
                >
                  {copyState === 'copied'
                    ? t.common.copied
                    : copyState === 'failed'
                      ? t.stopwatch.copyFailed
                      : t.stopwatch.copyLaps}
                </Button>
              </div>

              <div className="mt-3 max-h-72 overflow-y-auto rounded-md border bg-background">
                <div className="grid grid-cols-[2.5rem_1fr_1fr] gap-2 border-b px-3 py-2 text-xs font-medium text-muted-foreground sm:grid-cols-[4.5rem_1fr_1fr] sm:gap-3">
                  <span>{t.stopwatch.lap}</span>
                  <span className="text-right">{t.stopwatch.total}</span>
                  <span className="text-right">{t.stopwatch.split}</span>
                </div>
                <ol className="divide-y">
                  {laps.map((lap) => (
                    <li
                      key={lap.number}
                      className="grid grid-cols-[2.5rem_1fr_1fr] gap-2 px-3 py-2 font-mono text-xs tabular-nums sm:grid-cols-[4.5rem_1fr_1fr] sm:gap-3 sm:text-sm"
                    >
                      <span className="font-semibold text-foreground">
                        #{lap.number}
                      </span>
                      <span className="text-right">
                        {formatLapTime(lap.elapsedMs)}
                      </span>
                      <span className="text-right text-muted-foreground">
                        {formatLapTime(lap.splitMs)}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            </section>
          )}
        </CardContent>
      </Card>

      <section className="mt-6 rounded-lg border bg-muted/30 p-4">
        <p className="text-sm leading-6 text-muted-foreground">
          {t.stopwatch.hint}
        </p>
        <h2 className="mt-3 text-sm font-semibold tracking-normal">
          {t.stopwatch.accuracyTitle}
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {t.stopwatch.accuracyDescription}
        </p>
      </section>

      <section className="mt-10 border-t pt-8">
        <h2 className="text-xl font-semibold tracking-normal">{t.stopwatch.howTitle}</h2>
        <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-6 text-muted-foreground">
          {t.stopwatch.howTo.map((step) => <li key={step}>{step}</li>)}
        </ol>
        <h2 className="mt-8 text-xl font-semibold tracking-normal">{t.stopwatch.faqTitle}</h2>
        <dl className="mt-4 space-y-4">
          {t.stopwatch.faq.map((item) => (
            <div key={item.question}>
              <dt className="font-medium">{item.question}</dt>
              <dd className="mt-1 text-sm leading-6 text-muted-foreground">{item.answer}</dd>
            </div>
          ))}
        </dl>
        <h2 className="mt-8 text-xl font-semibold tracking-normal">{t.stopwatch.relatedTitle}</h2>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {t.stopwatch.related.map((item) => (
            <a key={item.href} href={item.href} className="block rounded-md border bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent">
              <span className="text-sm font-medium">{item.title}</span>
              <span className="mt-1 block text-xs leading-5 text-muted-foreground">{item.description}</span>
            </a>
          ))}
        </div>
      </section>
    </div>
  )
}
